/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';
import React, { useState, useEffect, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSelector, shallowEqual } from 'react-redux';

import { RootState } from '@ibm-watson/shared/ts/state';
import { useAppSelector } from '../../../../../store';
import { selectIsGenesysAudioConnectorInCpd } from '../../../../ia/store/features/selectors';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import {
  selectTextToSpeechPitchPercentage,
  selectTextToSpeechRatePercentage
} from '../../../store/phone/selectors';
import {
  selectTextToSpeechVoices,
  selectTextToSpeechInstanceLoaded,
  selectTextToSpeechVoicesByLanguage
} from '../../../store/voice/selectors';
import {
  DEFAULT_ENGLISH_US_TEXT_TO_SPEECH_MODEL,
  DEFAULT_ENGLISH_US_TEXT_TO_SPEECH_MODEL_CPD,
  VOICE_PITCH,
  VOICE_SPEED
} from '../constants';
import { FIELDS } from '../schema';
import { ControlledDropdownVoiceSelector } from '../speech-common/ControlledDropdownVoiceSelector';
import { ControlledSliderVoiceSelector } from '../speech-common/ControlledSliderVoiceSelector';
import { TextToSpeech as messages } from './messages';

export const Voices: React.FC<{ id?: string }> = () => {
  const { formatMessage: t } = useIntl();

  const isGenesysAudioConnectorEnabledInCpd = useAppSelector(
    selectIsGenesysAudioConnectorInCpd
  );

  const formMethods = useFormContext();
  const { getFieldValue, updateFieldValue } = useFormHelpers(formMethods);

  const [voices, setVoices] = useState([]);

  const pitchPercentage = useSelector(
    (state: RootState) => selectTextToSpeechPitchPercentage(state),
    shallowEqual
  );

  const ratePercentage = useSelector(
    (state: RootState) => selectTextToSpeechRatePercentage(state),
    shallowEqual
  );

  const [defaultVoiceSpeed] = useState(
    _.toNumber(getFieldValue(FIELDS.TTS.RATE_PERCENTAGE)) ||
      _.toNumber(ratePercentage)
  );
  const [defaultVoicePitch] = useState(
    _.toNumber(getFieldValue(FIELDS.TTS.PITCH_PERCENTAGE)) ||
      _.toNumber(pitchPercentage)
  );

  const ttsVoiceId = getFieldValue(FIELDS.TTS.VOICE);

  const allVoices = useSelector(selectTextToSpeechVoices, shallowEqual);
  const voicesByLanguage = useSelector(
    (state: RootState) => selectTextToSpeechVoicesByLanguage(state, ttsVoiceId),
    shallowEqual
  );
  const isTtsLoaded = useSelector(
    selectTextToSpeechInstanceLoaded,
    shallowEqual
  );

  // Set the field value based on selection
  const handleSelection = useCallback(
    selectedItem => {
      updateFieldValue(FIELDS.TTS.VOICE, selectedItem.name);
    },
    [updateFieldValue]
  );

  const handleLanguageSelection = useCallback(
    selectedItem => {
      updateFieldValue(FIELDS.TTS.VOICE, selectedItem.name);
    },
    [updateFieldValue]
  );

  const handleSpeedSelection = useCallback(
    value => {
      updateFieldValue(FIELDS.TTS.RATE_PERCENTAGE, value.toString());
    },
    [updateFieldValue]
  );

  const handlePitchSelection = useCallback(
    value => {
      updateFieldValue(FIELDS.TTS.PITCH_PERCENTAGE, value.toString());
    },
    [updateFieldValue]
  );

  useEffect(() => {
    if (
      allVoices?.length > 0 &&
      !allVoices.find(voice => voice.name === ttsVoiceId)
    ) {
      updateFieldValue(
        FIELDS.TTS.VOICE,
        isGenesysAudioConnectorEnabledInCpd
          ? DEFAULT_ENGLISH_US_TEXT_TO_SPEECH_MODEL_CPD
          : DEFAULT_ENGLISH_US_TEXT_TO_SPEECH_MODEL
      );
    }
  }, [
    allVoices,
    updateFieldValue,
    ttsVoiceId,
    isGenesysAudioConnectorEnabledInCpd
  ]);

  useEffect(() => {
    setVoices(allVoices);
  }, [allVoices]);

  return (
    <>
      <h4 className="productive-heading-02">
        <FormattedMessage {...messages.configureVoiceTitle} />
      </h4>
      <p className="TabContent__subtitle">
        <FormattedMessage {...messages.configureVoiceDescription} />
      </p>
      <div className="TextToSpeech__field-container">
        <ControlledDropdownVoiceSelector
          field={FIELDS.TTS.VOICE}
          id="tts-languages"
          instanceLoaded={isTtsLoaded}
          items={voices}
          labelText={t(messages.ttsVoiceLabel)}
          onChange={handleLanguageSelection}
          titleText={t(messages.ttsVoiceLanguageTitle)}
        />
        <ControlledDropdownVoiceSelector
          field={FIELDS.TTS.VOICE}
          id="tts-voices"
          instanceLoaded={isTtsLoaded}
          items={voicesByLanguage}
          labelText={t(messages.ttsVoiceLabel)}
          onChange={handleSelection}
          selectedLanguage={ttsVoiceId}
          titleText={t(messages.ttsVoiceTitle)}
        />
        <div className="TextToSpeech__voice-sliders">
          <ControlledSliderVoiceSelector
            className="TextToSpeech__voice-sliders"
            defaultValue={defaultVoiceSpeed}
            id="tts-speed"
            instanceLoaded={isTtsLoaded}
            labelText={t(messages.ttsVoiceSpeedSliderLabel)}
            maxValue={VOICE_SPEED.MAX}
            minValue={VOICE_SPEED.MIN}
            onChange={handleSpeedSelection}
            stepValue={VOICE_SPEED.STEP}
          />
          <ControlledSliderVoiceSelector
            className="TextToSpeech__voice-sliders"
            defaultValue={defaultVoicePitch}
            id="tts-pitch"
            instanceLoaded={isTtsLoaded}
            labelText={t(messages.ttsVoicePitchSliderLabel)}
            maxValue={VOICE_PITCH.MAX}
            minValue={VOICE_PITCH.MIN}
            onChange={handlePitchSelection}
            stepValue={VOICE_PITCH.STEP}
          />
        </div>
      </div>
    </>
  );
};
